import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { authClient } from "../lib/auth-client";
import { NOT_DASHBOARD_ACCOUNT_EVENT, SESSION_ENDED_EVENT } from "../lib/access-events";
import {
  settleProbe,
  resolveAuthState,
  type AuthStatus,
  type AuthStatusProbe,
  type PermissionsStatus,
} from "../lib/access-state";
import { type TabKey } from "../lib/tab-registry";

interface AuthContextType {
  isAuthenticated: boolean;
  user: string | null;
  userId: string | null;
  /** Internal staff (`account_type === "admin"`). Entry to the dashboard needs this AND a grant. */
  isAdmin: boolean;
  /**
   * Tabs this user may view, straight off the et-auth session (`permissions`).
   * Non-staff always get []. null only while the session is loading.
   */
  allowedTabs: TabKey[] | null;
  /** Whether the dashboard could read the session at all. */
  authStatus: AuthStatus;
  /** Whether et-auth could read the tab grants of an admin session. */
  permissionsStatus: PermissionsStatus;
  login: (
    email: string,
    password: string,
  ) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  /** Re-read the session so a grant change shows up without a reload. */
  refreshSession: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Ask the server which of the three states an empty session is in. */
export type FetchAuthStatus = () => Promise<AuthStatusProbe>;

export const AUTH_STATUS_PATH = "/api/auth/status";

const defaultFetchAuthStatus: FetchAuthStatus = async () => {
  try {
    const response = await fetch(AUTH_STATUS_PATH, { credentials: "include" });
    if (!response.ok) return "unavailable";
    const body = (await response.json()) as { status?: unknown };
    return body.status === "ok" || body.status === "none" ? body.status : "unavailable";
  } catch {
    return "unavailable";
  }
};

interface AuthProviderProps {
  children: ReactNode;
  /** Injected by tests; the browser uses the fetch above. */
  fetchAuthStatus?: FetchAuthStatus;
}

export const AuthProvider = ({
  children,
  fetchAuthStatus = defaultFetchAuthStatus,
}: AuthProviderProps) => {
  const session = authClient.useSession();
  const isAuthenticated = Boolean(session.data?.user);
  const user = session.data?.user?.email || session.data?.user?.name || null;
  const userId = session.data?.user?.id || null;
  const isAdmin = session.data?.user?.account_type === "admin";
  const isLoading = session.isPending ?? false;

  // An empty session means "signed out" or "et-auth is down"; better-auth
  // answers null to both. The server probe tells the two apart, so an outage
  // never sends a signed-in user back to the login form.
  const [probe, setProbe] = useState<AuthStatusProbe | null>(null);
  const [probeTick, setProbeTick] = useState(0);
  const needsProbe = !isLoading && !isAuthenticated;
  // Drop a stale answer the moment the session state changes. Adjusting state
  // during render is the React-endorsed shape for "derive from a prop".
  const [probedFor, setProbedFor] = useState(needsProbe);
  if (needsProbe !== probedFor) {
    setProbedFor(needsProbe);
    setProbe(null);
  }
  const { refetch: refetchSession } = session;
  useEffect(() => {
    if (!needsProbe) return;
    let cancelled = false;
    void fetchAuthStatus()
      .then((status) =>
        settleProbe(
          status,
          () => refetchSession({ query: { disableCookieCache: true } }),
          () => cancelled,
        ),
      )
      .then((settled) => {
        if (!cancelled && settled) setProbe(settled);
      });
    return () => {
      cancelled = true;
    };
  }, [needsProbe, probeTick, fetchAuthStatus, refetchSession]);

  // et-auth attaches `permissions` and `permissionsStatus` to admin sessions
  // only. The server guards read the same fields, so what the sidebar shows
  // and what the API allows cannot drift.
  const root = session.data as
    | { permissions?: unknown; permissionsStatus?: unknown }
    | null
    | undefined;
  const { authStatus, permissionsStatus, allowedTabs } = resolveAuthState({
    isLoading,
    hasUser: isAuthenticated,
    isAdmin,
    permissions: root?.permissions,
    sessionPermissionsStatus: root?.permissionsStatus,
    probe,
  });

  const login = async (email: string, password: string) => {
    try {
      const { error } = await authClient.signIn.username({
        username: email,
        password,
      });
      if (error) {
        return { ok: false, error: error.message };
      }
      // Refetch session to ensure state is updated before returning
      await session.refetch();
      return { ok: true };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to sign in";
      return { ok: false, error: message };
    }
  };

  const logout = async () => {
    await authClient.signOut();
  };

  // Bypass the 5-minute cookie cache so a grant made seconds ago shows up;
  // the server guards read the session the same way. The status probe runs
  // again too, so a retry during an outage can clear it.
  const { refetch } = session;
  const refreshSession = useCallback(async () => {
    setProbeTick((tick) => tick + 1);
    await authClient.getSession({ query: { disableCookieCache: true } });
    await refetch({ query: { disableCookieCache: true } });
  }, [refetch]);

  // A demotion lands as a 403 on whatever call the open tab made next. Read
  // the session again so the "not a dashboard account" screen replaces the
  // page by itself, with no reload.
  useEffect(() => {
    const onDemoted = () => void refreshSession();
    window.addEventListener(NOT_DASHBOARD_ACCOUNT_EVENT, onDemoted);
    // A revoked session reads as empty, and the probe then shows login.
    window.addEventListener(SESSION_ENDED_EVENT, onDemoted);
    return () => {
      window.removeEventListener(NOT_DASHBOARD_ACCOUNT_EVENT, onDemoted);
      window.removeEventListener(SESSION_ENDED_EVENT, onDemoted);
    };
  }, [refreshSession]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        userId,
        isAdmin,
        allowedTabs,
        authStatus,
        permissionsStatus,
        login,
        logout,
        refreshSession,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
